// @ts-nocheck
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../components/layout/PageHeader";
import { StatCard } from "../components/StatCard";
import { useLang } from "../lib/lang";

const API = import.meta.env.VITE_API_URL ?? "";

interface Reply {
  id: number; keyword: string; type: "text" | "template"; response: string; deviceId: number | null; active: boolean; createdAt: string;
}

async function fetchReplies(): Promise<Reply[]> {
  const r = await fetch(`${API}/api/replies`); return r.json();
}

export function Chatbot() {
  const qc = useQueryClient();
  const { t } = useLang();
  const d = t.chatbot;

  const { data: replies = [], isLoading } = useQuery({ queryKey: ["replies"], queryFn: fetchReplies });
  const [showForm, setShowForm] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [response, setResponse] = useState("");
  const [type, setType] = useState<"text" | "template">("text");

  const create = useMutation({
    mutationFn: async (body: object) => { const r = await fetch(`${API}/api/replies`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }); return r.json(); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["replies"] }),
  });
  const toggle = useMutation({
    mutationFn: async ({ id, active }: { id: number; active: boolean }) => { const r = await fetch(`${API}/api/replies/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ active }) }); return r.json(); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["replies"] }),
  });
  const del = useMutation({
    mutationFn: async (id: number) => { await fetch(`${API}/api/replies/${id}`, { method: "DELETE" }); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["replies"] }),
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    await create.mutateAsync({ keyword, type, response });
    setKeyword(""); setResponse(""); setType("text"); setShowForm(false);
  };

  const totalReplies = replies.length;
  const templateReplies = replies.filter(r => r.type === "template").length;
  const textReplies = replies.filter(r => r.type === "text").length;

  return (
    <div>
      <PageHeader title={d.title} actions={[{ label: d.newRule, icon: "fas fa-plus", onClick: () => setShowForm(true) }]} />

      <div className="px-6 pb-6 space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <StatCard label={d.totalRules} value={totalReplies} iconClass="fas fa-robot" gradient="primary" />
          <StatCard label={`${d.type}: ${d.template}`} value={templateReplies} iconClass="fas fa-file-alt" gradient="info" />
          <StatCard label={`${d.type}: ${d.text}`} value={textReplies} iconClass="fas fa-comment-alt" gradient="success" />
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#e9ecef]">
                <h5 className="font-semibold text-[#32325d]">{d.newRule}</h5>
                <button onClick={() => setShowForm(false)} className="text-[#8898aa] hover:text-[#32325d]"><i className="fas fa-times" /></button>
              </div>
              <form onSubmit={handleCreate} className="p-6 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.keyword}</label>
                  <input value={keyword} onChange={e => setKeyword(e.target.value)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" placeholder={d.keywordPlaceholder} required />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.type}</label>
                  <select value={type} onChange={e => setType(e.target.value as any)} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]">
                    <option value="text">{d.text}</option>
                    <option value="template">{d.template}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.response}</label>
                  <textarea value={response} onChange={e => setResponse(e.target.value)} rows={4} className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef] resize-none" placeholder={d.responsePlaceholder} required />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-[#e9ecef] rounded-lg text-[#525f7f]">{t.common.cancel}</button>
                  <button type="submit" disabled={create.isPending} className="px-4 py-2 text-sm gradient-primary text-white rounded-lg disabled:opacity-50">
                    {create.isPending ? t.common.saving : d.newRule}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef] p-12 text-center"><i className="fas fa-spinner fa-spin text-2xl text-[#1171ef]" /></div>
        ) : replies.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef] p-12 text-center">
            <i className="fas fa-robot text-4xl text-[#cad1d7] mb-3 block" />
            <p className="text-[#8898aa] text-sm mb-1">{d.noRules}</p>
            <p className="text-[#adb5bd] text-xs mb-4">{d.noRulesHint}</p>
            <button onClick={() => setShowForm(true)} className="gradient-primary text-white px-5 py-2 rounded-lg text-sm shadow-sm"><i className="fas fa-plus me-2" />{d.newRule}</button>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef]">
            <div className="px-5 py-4 border-b border-[#e9ecef]">
              <h4 className="text-sm font-semibold text-[#32325d]">{d.title}</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    {[d.keyword, d.type, d.response, t.common.status, t.common.actions].map(h => (
                      <th key={h} className="px-5 py-3 text-start text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {replies.map(r => (
                    <tr key={r.id} className="border-b border-[#f6f9fc] hover:bg-[#f8f9fe]">
                      <td className="px-5 py-3"><code className="text-xs bg-[#e9ecef] px-2 py-0.5 rounded font-mono text-[#525f7f]">{r.keyword}</code></td>
                      <td className="px-5 py-3">
                        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${r.type === "template" ? "bg-[#cce5ff] text-[#004085]" : "bg-[#eef0fd] text-[#1171ef]"}`}>
                          {r.type === "template" ? d.template : d.text}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-sm text-[#525f7f] max-w-[200px] truncate">{r.response}</td>
                      <td className="px-5 py-3">
                        <button onClick={() => toggle.mutate({ id: r.id, active: !r.active })} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${r.active ? "bg-[#2dce89]" : "bg-[#cad1d7]"}`}>
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow ${r.active ? "translate-x-4" : "translate-x-1"}`} />
                        </button>
                      </td>
                      <td className="px-5 py-3">
                        <button onClick={() => del.mutate(r.id)} className="text-[#f5365c] hover:text-red-700 text-sm"><i className="fas fa-trash" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
